import { getAccessToken } from './auth0-client';

export type SubscriptionTier = 'core' | 'pro' | 'ultra';

/** Shape returned by subscription-manager GET /v1/entitlements/me. */
export interface MyEntitlements {
  tier: string;
  status: string;
  grants: string[];
  cancelsAt: string | null;
}

const TIER_LABELS: Record<SubscriptionTier, string> = {
  core: 'Core',
  pro: 'Pro',
  ultra: 'Ultra',
};

/**
 * Maps a raw tier key (core|pro|ultra) to its display label. Unknown or missing
 * tiers fall back to 'Core' — the backend's documented no-subscription default —
 * so the UI never renders a raw lowercase identifier.
 */
export function tierLabel(tier?: string): string {
  return TIER_LABELS[(tier ?? '') as SubscriptionTier] ?? 'Core';
}

/**
 * Fetches the authenticated caller's own subscription entitlements. Mirrors the
 * auth-fetch pattern in use-checkout: the user's bearer token from
 * getAccessToken() + the subscription-manager base URL (passed by the caller so
 * each app supplies its own configured URL).
 */
export async function fetchMyEntitlements(baseUrl: string): Promise<MyEntitlements> {
  const token = await getAccessToken();
  if (!token) throw new Error('No authenticated session');
  const response = await fetch(`${baseUrl}/v1/entitlements/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error(`Entitlements request failed (${response.status})`);
  return (await response.json()) as MyEntitlements;
}
